import { useMemo, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  Box,
  Clock,
  Database,
  ExternalLink,
  Globe,
  HardDrive,
  Layers,
  Link2,
  Loader2,
  Network,
  RefreshCw,
  RotateCcw,
  Search,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { usePlatformModel, usePlatformModelRefresh } from '@/hooks/useApi'
import { Card, CardContent } from '@/components/ui/card'
import type {
  PlatformModel,
  PlatformModelDeclaredBinding,
  PlatformModelItem,
} from '@/types'

type NodeStatus = 'LIVE' | 'CONFIGURED' | 'UNKNOWN'
type NodeKind = 'worker' | 'resource'

interface TopologyNode {
  id: string
  label: string
  target: string
  kind: NodeKind
  resourceType: string
  status: NodeStatus
  workersDev?: boolean
  routePatterns?: string[]
  degree: number
  zone: 'edge' | 'hub' | 'service' | 'resource'
  x: number
  y: number
}

interface TopologyEdge {
  id: string
  from: string
  to: string
  type: string
  bindingName: string
}

interface TopologyData {
  nodes: TopologyNode[]
  edges: TopologyEdge[]
  resourceCount: number
  serviceEdgeCount: number
}

const CANVAS_WIDTH = 1280
const CANVAS_HEIGHT = 520

const ZONES = {
  edge: { x: 24, y: 54, width: 190, height: 418, label: 'OPS Ingress' },
  hub: { x: 230, y: 54, width: 280, height: 418, label: 'Connected Hubs' },
  service: { x: 526, y: 54, width: 374, height: 418, label: 'Platform Services' },
  resource: { x: 916, y: 54, width: 340, height: 418, label: 'Data & Resources' },
} as const

function timeAgo(iso?: string): string {
  if (!iso) return 'unknown'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'unknown'
  const diffMs = Date.now() - date.getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function shortLabel(value: string, max = 22): string {
  if (value.length <= max) return value
  return `${value.slice(0, max - 1)}…`
}

function looksOpaque(value: string): boolean {
  return /^[0-9a-f-]{16,}$/i.test(value)
}

function bindingResourceType(type: string): string {
  const normalized = type.toLowerCase()
  if (normalized === 'd1' || normalized === 'd1_database') return 'd1'
  if (normalized === 'kv_namespace') return 'kv'
  if (normalized === 'r2_bucket') return 'r2'
  if (normalized === 'vectorize' || normalized === 'vectorize_index') return 'vectorize'
  if (normalized === 'durable_object_namespace') return 'durable_object'
  if (normalized === 'ai') return 'ai'
  if (normalized.includes('queue')) return 'queue'
  return normalized
}

function resourceLabel(binding: PlatformModelDeclaredBinding): string {
  if (!binding.target) return binding.name
  return looksOpaque(binding.target) ? binding.name : binding.target
}

function nodePalette(node: TopologyNode) {
  if (node.kind === 'worker') {
    if (node.status === 'UNKNOWN') return { fill: '#64748b', stroke: '#94a3b8', glow: '#64748b' }
    if (node.zone === 'edge') return { fill: '#2563eb', stroke: '#60a5fa', glow: '#3b82f6' }
    if (node.zone === 'hub') return { fill: '#7c3aed', stroke: '#c4b5fd', glow: '#8b5cf6' }
    return { fill: '#5b21b6', stroke: '#a78bfa', glow: '#7c3aed' }
  }

  const palette: Record<string, { fill: string; stroke: string; glow: string }> = {
    d1: { fill: '#059669', stroke: '#6ee7b7', glow: '#10b981' },
    kv: { fill: '#0891b2', stroke: '#67e8f9', glow: '#06b6d4' },
    r2: { fill: '#d97706', stroke: '#fcd34d', glow: '#f59e0b' },
    vectorize: { fill: '#db2777', stroke: '#f9a8d4', glow: '#ec4899' },
    durable_object: { fill: '#7c3aed', stroke: '#c4b5fd', glow: '#8b5cf6' },
    ai: { fill: '#4f46e5', stroke: '#a5b4fc', glow: '#6366f1' },
    queue: { fill: '#ea580c', stroke: '#fdba74', glow: '#f97316' },
  }
  return palette[node.resourceType] || { fill: '#475569', stroke: '#94a3b8', glow: '#64748b' }
}

function edgeStroke(type: string): string {
  const normalized = bindingResourceType(type)
  if (type === 'service') return '#8b5cf6'
  if (normalized === 'd1') return '#10b981'
  if (normalized === 'kv') return '#06b6d4'
  if (normalized === 'r2') return '#f59e0b'
  if (normalized === 'vectorize') return '#ec4899'
  if (normalized === 'ai') return '#6366f1'
  if (normalized === 'queue') return '#f97316'
  return '#64748b'
}

function ModelStatusBadge({ status }: { status: string }) {
  const config = {
    current: { color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Current' },
    stale: { color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Stale' },
    failed: { color: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Failed' },
  }[status] || { color: 'bg-surface-elevated text-text-muted border-border', label: status }

  return <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${config.color}`}>{config.label}</span>
}

function NodeStatusBadge({ status }: { status: NodeStatus }) {
  const cls = status === 'LIVE'
    ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500'
    : status === 'CONFIGURED'
      ? 'border-blue-500/20 bg-blue-500/10 text-blue-500'
      : 'border-amber-500/20 bg-amber-500/10 text-amber-500'
  return <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold tracking-wide ${cls}`}>{status}</span>
}

function StatCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
  detail: string
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-violet-500/10 bg-white/72 p-4 shadow-[0_12px_35px_rgba(76,29,149,0.06)] backdrop-blur-xl dark:bg-slate-950/45">
      <div className="absolute -right-7 -top-7 h-20 w-20 rounded-full bg-violet-500/8 blur-2xl" />
      <div className="relative flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-violet-500/15 bg-violet-500/10 text-violet-500">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
          <div className="mt-0.5 text-2xl font-semibold tracking-tight text-text-primary">{value}</div>
          <div className="mt-0.5 truncate text-[10px] text-text-muted">{detail}</div>
        </div>
      </div>
    </div>
  )
}

function placeGroup(
  nodes: TopologyNode[],
  zone: { x: number; y: number; width: number; height: number },
  maxColumns: number,
) {
  if (nodes.length === 0) return
  const columns = Math.min(maxColumns, Math.max(1, Math.ceil(nodes.length / 11)))
  const rows = Math.ceil(nodes.length / columns)
  const xStep = zone.width / columns
  const yStep = zone.height / Math.max(rows, 1)

  nodes.forEach((node, index) => {
    const column = Math.floor(index / rows)
    const row = index % rows
    node.x = zone.x + xStep * column + xStep / 2
    node.y = zone.y + yStep * row + yStep / 2
  })
}

function buildTopology(data: PlatformModel): TopologyData {
  const nodes = new Map<string, TopologyNode>()
  const edges: TopologyEdge[] = []

  Object.entries(data.items || {}).forEach(([name, item]) => {
    nodes.set(`worker:${name}`, {
      id: `worker:${name}`,
      label: name,
      target: name,
      kind: 'worker',
      resourceType: 'worker',
      status: item.item_status === 'LIVE' ? 'LIVE' : 'UNKNOWN',
      workersDev: item.workers_dev === true,
      routePatterns: (item.routes || []).map((route) => route.pattern),
      degree: 0,
      zone: 'service',
      x: 0,
      y: 0,
    })
  })

  Object.entries(data.items || {}).forEach(([workerName, item]) => {
    const from = `worker:${workerName}`
    ;(item.declared_bindings || []).forEach((binding) => {
      if (!binding.target) return

      const isService = binding.type === 'service'
      const resourceType = isService ? 'worker' : bindingResourceType(binding.type)
      const to = isService ? `worker:${binding.target}` : `${resourceType}:${binding.target}`

      if (!nodes.has(to)) {
        nodes.set(to, {
          id: to,
          label: isService ? binding.target : resourceLabel(binding),
          target: binding.target,
          kind: isService ? 'worker' : 'resource',
          resourceType,
          status: isService && data.items[binding.target] ? 'LIVE' : 'CONFIGURED',
          workersDev: isService ? data.items[binding.target]?.workers_dev === true : false,
          routePatterns: isService ? (data.items[binding.target]?.routes || []).map((route) => route.pattern) : [],
          degree: 0,
          zone: isService ? 'service' : 'resource',
          x: 0,
          y: 0,
        })
      }

      edges.push({
        id: `${from}|${binding.name}|${to}`,
        from,
        to,
        type: binding.type,
        bindingName: binding.name,
      })
    })
  })

  const workers = [...nodes.values()].filter((node) => node.kind === 'worker')
  const workerIds = new Set(workers.map((node) => node.id))
  const serviceEdges = edges.filter((edge) => edge.type === 'service' && workerIds.has(edge.from) && workerIds.has(edge.to))

  const degree = new Map<string, number>()
  const incomingService = new Map<string, number>()
  const directedService = new Map<string, Set<string>>()
  const undirectedService = new Map<string, Set<string>>()
  workers.forEach((node) => {
    directedService.set(node.id, new Set())
    undirectedService.set(node.id, new Set())
    incomingService.set(node.id, 0)
  })

  serviceEdges.forEach((edge) => {
    degree.set(edge.from, (degree.get(edge.from) || 0) + 1)
    degree.set(edge.to, (degree.get(edge.to) || 0) + 1)
    incomingService.set(edge.to, (incomingService.get(edge.to) || 0) + 1)
    directedService.get(edge.from)?.add(edge.to)
    undirectedService.get(edge.from)?.add(edge.to)
    undirectedService.get(edge.to)?.add(edge.from)
  })

  workers.forEach((node) => { node.degree = degree.get(node.id) || 0 })

  // The active OPS component is derived, not named: use the largest connected
  // service graph, then choose its highest-connectivity node as the central hub.
  // A routed edge worker counts as Ingress only when it has no service callers
  // and has a directed path into that hub. This makes the current admin gateway
  // appear automatically and will also admit a future tenant gateway once it is
  // genuinely wired into the same core path.
  const unvisited = new Set(workers.map((node) => node.id))
  const components: Set<string>[] = []
  while (unvisited.size) {
    const start = unvisited.values().next().value as string
    const component = new Set<string>()
    const stack = [start]
    unvisited.delete(start)
    while (stack.length) {
      const current = stack.pop() as string
      component.add(current)
      undirectedService.get(current)?.forEach((next) => {
        if (unvisited.has(next)) {
          unvisited.delete(next)
          stack.push(next)
        }
      })
    }
    components.push(component)
  }
  components.sort((a, b) => b.size - a.size)
  const primaryComponent = components[0] || new Set(workers.map((node) => node.id))
  const centralHubId = [...primaryComponent].sort((a, b) => (degree.get(b) || 0) - (degree.get(a) || 0))[0]

  const reachesCentralHub = (startId: string) => {
    if (!centralHubId || startId === centralHubId) return false
    const seen = new Set<string>([startId])
    const stack = [startId]
    while (stack.length) {
      const current = stack.pop() as string
      for (const next of directedService.get(current) || []) {
        if (next === centralHubId) return true
        if (!seen.has(next)) {
          seen.add(next)
          stack.push(next)
        }
      }
    }
    return false
  }

  const hasRouteEvidence = workers.some((node) => (node.routePatterns?.length || 0) > 0)
  const ingressCandidates = workers.filter((node) =>
    primaryComponent.has(node.id)
    && (node.routePatterns?.length || 0) > 0
    && (incomingService.get(node.id) || 0) === 0
    && reachesCentralHub(node.id)
  )

  const edgeNodes = (hasRouteEvidence ? ingressCandidates : workers.filter((node) => node.workersDev))
    .sort((a, b) => b.degree - a.degree || a.label.localeCompare(b.label))

  const connectedness = workers.map((node) => node.degree).sort((a, b) => a - b)
  const percentile = connectedness.length
    ? connectedness[Math.min(connectedness.length - 1, Math.floor(connectedness.length * 0.68))]
    : 0
  const hubThreshold = Math.max(3, percentile || 0)

  const edgeIds = new Set(edgeNodes.map((node) => node.id))
  const hubNodes = workers
    .filter((node) => !edgeIds.has(node.id) && node.degree >= hubThreshold)
    .sort((a, b) => b.degree - a.degree || a.label.localeCompare(b.label))

  const hubIds = new Set(hubNodes.map((node) => node.id))

  const serviceNodes = workers
    .filter((node) => !edgeIds.has(node.id) && !hubIds.has(node.id))
    .sort((a, b) => b.degree - a.degree || a.label.localeCompare(b.label))

  const resourceNodes = [...nodes.values()]
    .filter((node) => node.kind === 'resource')
    .sort((a, b) => a.resourceType.localeCompare(b.resourceType) || a.label.localeCompare(b.label))

  edgeNodes.forEach((node) => { node.zone = 'edge' })
  hubNodes.forEach((node) => { node.zone = 'hub' })
  serviceNodes.forEach((node) => { node.zone = 'service' })
  resourceNodes.forEach((node) => { node.zone = 'resource' })

  placeGroup(edgeNodes, ZONES.edge, 2)
  placeGroup(hubNodes, ZONES.hub, 2)
  placeGroup(serviceNodes, ZONES.service, 3)
  placeGroup(resourceNodes, ZONES.resource, 3)

  return {
    nodes: [...nodes.values()],
    edges,
    resourceCount: resourceNodes.length,
    serviceEdgeCount: edges.filter((edge) => edge.type === 'service').length,
  }
}

function nodeHalfWidth(node: TopologyNode): number {
  return node.kind === 'worker' ? 63 : 46
}

function distributedOffsets(
  edges: TopologyEdge[],
  groupBy: (edge: TopologyEdge) => string,
  sortBy: (edge: TopologyEdge) => number,
  maxSpan = 20,
) {
  const groups = new Map<string, TopologyEdge[]>()
  edges.forEach((edge) => {
    const key = groupBy(edge)
    const group = groups.get(key) || []
    group.push(edge)
    groups.set(key, group)
  })

  const offsets = new Map<string, number>()
  groups.forEach((group) => {
    group.sort((a, b) => sortBy(a) - sortBy(b) || a.id.localeCompare(b.id))
    const span = Math.min(maxSpan, Math.max(0, (group.length - 1) * 4))
    const step = group.length > 1 ? span / (group.length - 1) : 0
    group.forEach((edge, index) => {
      offsets.set(edge.id, (index - (group.length - 1) / 2) * step)
    })
  })
  return offsets
}

function buildEdgePaths(
  edges: TopologyEdge[],
  nodeById: Map<string, TopologyNode>,
): Map<string, string> {
  const sourceOffsets = distributedOffsets(
    edges,
    (edge) => edge.from,
    (edge) => nodeById.get(edge.to)?.y || 0,
    22,
  )
  const targetOffsets = distributedOffsets(
    edges,
    (edge) => edge.to,
    (edge) => nodeById.get(edge.from)?.y || 0,
    22,
  )

  // A family is every edge terminating at the same typed target. Those lines
  // share one routing direction/corridor (same DB, same worker, same KV, etc.)
  // but receive individual parallel offsets so they remain visually distinct.
  const families = new Map<string, TopologyEdge[]>()
  edges.forEach((edge) => {
    const key = `${edge.type}|${edge.to}`
    const group = families.get(key) || []
    group.push(edge)
    families.set(key, group)
  })

  const familyLane = new Map<string, number>()
  families.forEach((group) => {
    group.sort((a, b) => {
      const ay = nodeById.get(a.from)?.y || 0
      const by = nodeById.get(b.from)?.y || 0
      return ay - by || a.id.localeCompare(b.id)
    })
    const step = group.length > 1 ? Math.min(6, 30 / (group.length - 1)) : 0
    group.forEach((edge, index) => {
      familyLane.set(edge.id, (index - (group.length - 1) / 2) * step)
    })
  })

  const paths = new Map<string, string>()
  edges.forEach((edge) => {
    const from = nodeById.get(edge.from)
    const to = nodeById.get(edge.to)
    if (!from || !to) return

    const horizontalDirection = to.x >= from.x ? 1 : -1
    const sourcePort = sourceOffsets.get(edge.id) || 0
    const targetPort = targetOffsets.get(edge.id) || 0
    const lane = familyLane.get(edge.id) || 0

    const sx = from.x + horizontalDirection * nodeHalfWidth(from)
    const tx = to.x - horizontalDirection * nodeHalfWidth(to)
    const sy = from.y + sourcePort
    const ty = to.y + targetPort
    const dx = tx - sx

    if (Math.abs(dx) < 90) {
      // Same-column relationships travel around the outside of the column
      // rather than cutting vertically through intermediate nodes.
      const side = to.zone === 'resource' ? -1 : 1
      const detourX = (horizontalDirection > 0 ? Math.max(sx, tx) : Math.min(sx, tx))
        + side * (58 + Math.abs(lane))
      const c1x = detourX
      const c2x = detourX
      paths.set(edge.id, `M ${sx} ${sy} C ${c1x} ${sy}, ${c2x} ${ty}, ${tx} ${ty}`)
      return
    }

    // Fan target families away from the vertical centre of their destination
    // zone. Connections to the same target therefore arc in the same
    // direction, while separate target families naturally occupy different
    // visual lanes. Individual lines remain separate throughout.
    const zone = ZONES[to.zone]
    const zoneCentre = zone.y + zone.height / 2
    const fanDirection = to.y < zoneCentre ? -1 : 1
    const distanceFromCentre = Math.abs(to.y - zoneCentre)
    const familyArc = fanDirection * Math.min(82, 30 + distanceFromCentre * 0.22)
    const corridorY = (sy + ty) / 2 + familyArc + lane
    const mx = sx + dx * 0.52

    const c1x = sx + dx * 0.22
    const c2x = mx - dx * 0.13
    const c3x = mx + dx * 0.13
    const c4x = tx - dx * 0.22

    paths.set(
      edge.id,
      `M ${sx} ${sy} C ${c1x} ${sy}, ${c2x} ${corridorY}, ${mx} ${corridorY} C ${c3x} ${corridorY}, ${c4x} ${ty}, ${tx} ${ty}`,
    )
  })

  return paths
}

function TopologyGraph({
  data,
  selectedId,
  onSelect,
}: {
  data: PlatformModel
  selectedId: string
  onSelect: (id: string) => void
}) {
  const [search, setSearch] = useState('')
  const [showResources, setShowResources] = useState(true)
  const [focusMode, setFocusMode] = useState(false)
  const [zoom, setZoom] = useState(1)

  const topology = useMemo(() => buildTopology(data), [data])
  const nodeById = useMemo(() => new Map(topology.nodes.map((node) => [node.id, node])), [topology.nodes])
  const selected = nodeById.get(selectedId)

  const connected = useMemo(() => {
    const ids = new Set<string>([selectedId])
    topology.edges.forEach((edge) => {
      if (edge.from === selectedId) ids.add(edge.to)
      if (edge.to === selectedId) ids.add(edge.from)
    })
    return ids
  }, [selectedId, topology.edges])

  const searchTerm = search.trim().toLowerCase()
  const visibleNodes = topology.nodes.filter((node) => showResources || node.kind === 'worker')
  const visibleIds = new Set(visibleNodes.map((node) => node.id))
  const visibleEdges = topology.edges.filter((edge) => visibleIds.has(edge.from) && visibleIds.has(edge.to))
  const routedPaths = useMemo(() => buildEdgePaths(visibleEdges, nodeById), [visibleEdges, nodeById])

  const viewWidth = CANVAS_WIDTH / zoom
  const viewHeight = CANVAS_HEIGHT / zoom
  const viewX = (CANVAS_WIDTH - viewWidth) / 2
  const viewY = (CANVAS_HEIGHT - viewHeight) / 2

  const zoneCounts = useMemo(() => {
    const counts = { edge: 0, hub: 0, service: 0, resource: 0 }
    visibleNodes.forEach((node) => { counts[node.zone]++ })
    return counts
  }, [visibleNodes])

  const isNodeActive = (node: TopologyNode) => {
    if (focusMode && !connected.has(node.id)) return false
    if (searchTerm && !node.label.toLowerCase().includes(searchTerm) && !node.target.toLowerCase().includes(searchTerm)) return false
    return true
  }

  return (
    <Card className="overflow-hidden border-violet-500/10 bg-white/64 p-0 shadow-[0_22px_60px_rgba(76,29,149,0.07)] backdrop-blur-xl dark:bg-slate-950/42">
      <div className="flex flex-col gap-3 border-b border-violet-500/10 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Network className="h-4.5 w-4.5 text-violet-500" />
            <h3 className="text-sm font-semibold text-text-primary">Graph / Topographic View</h3>
          </div>
          <p className="mt-1 text-[11px] text-text-muted">Live workers and declared resources, positioned from current connectivity rather than a fixed worker list.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="relative min-w-[210px] flex-1 lg:flex-none">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find in graph..."
              className="h-9 w-full rounded-xl border border-border bg-surface/70 pl-8 pr-3 text-xs text-text-primary outline-none transition focus:border-violet-500/40 lg:w-[230px]"
            />
          </label>
          <button
            onClick={() => setShowResources((value) => !value)}
            className={`flex h-9 items-center gap-1.5 rounded-xl border px-3 text-[11px] font-medium transition ${showResources ? 'border-violet-500/25 bg-violet-500/10 text-violet-500' : 'border-border bg-surface/70 text-text-muted'}`}
          >
            <Layers className="h-3.5 w-3.5" /> Resources
          </button>
          <button
            onClick={() => setFocusMode((value) => !value)}
            className={`h-9 rounded-xl border px-3 text-[11px] font-medium transition ${focusMode ? 'border-violet-500/25 bg-violet-500/10 text-violet-500' : 'border-border bg-surface/70 text-text-muted'}`}
          >
            Focus
          </button>
          <div className="flex h-9 items-center overflow-hidden rounded-xl border border-border bg-surface/70">
            <button onClick={() => setZoom((z) => Math.max(0.75, Number((z - 0.15).toFixed(2))))} className="grid h-9 w-9 place-items-center text-text-muted hover:text-text-primary" title="Zoom out">
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <span className="min-w-11 text-center text-[10px] text-text-muted">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(1.65, Number((z + 0.15).toFixed(2))))} className="grid h-9 w-9 place-items-center text-text-muted hover:text-text-primary" title="Zoom in">
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setZoom(1)} className="grid h-9 w-9 place-items-center border-l border-border text-text-muted hover:text-text-primary" title="Reset zoom">
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div
        className="relative overflow-x-auto"
        style={{
          backgroundImage:
            'radial-gradient(circle at 18% 16%, rgba(124,58,237,.08), transparent 28%), radial-gradient(circle at 78% 68%, rgba(59,130,246,.07), transparent 30%), linear-gradient(180deg, rgba(255,255,255,.28), transparent)',
        }}
      >
        <div className="min-w-[1080px]">
          <svg
            viewBox={`${viewX} ${viewY} ${viewWidth} ${viewHeight}`}
            className="h-[520px] w-full select-none"
            role="img"
            aria-label="Interactive platform topology"
          >
            <defs>
              <pattern id="pm-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                <path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeOpacity="0.035" strokeWidth="1" />
              </pattern>
              <filter id="pm-node-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            <rect width={CANVAS_WIDTH} height={CANVAS_HEIGHT} fill="url(#pm-grid)" className="text-violet-500" />

            {Object.entries(ZONES).map(([zoneId, zone]) => {
              if (zoneId === 'resource' && !showResources) return null
              const count = zoneCounts[zoneId as keyof typeof zoneCounts]
              return (
                <g key={zoneId}>
                  <rect
                    x={zone.x}
                    y={zone.y}
                    width={zone.width}
                    height={zone.height}
                    rx="28"
                    fill="currentColor"
                    fillOpacity="0.022"
                    stroke="currentColor"
                    strokeOpacity="0.12"
                    strokeDasharray="5 7"
                    className="text-violet-500"
                  />
                  <ellipse
                    cx={zone.x + zone.width / 2}
                    cy={zone.y + zone.height / 2}
                    rx={Math.max(30, zone.width / 2 - 16)}
                    ry={Math.max(30, zone.height / 2 - 28)}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.045"
                    className="text-violet-500"
                  />
                  <ellipse
                    cx={zone.x + zone.width / 2}
                    cy={zone.y + zone.height / 2}
                    rx={Math.max(20, zone.width / 2 - 34)}
                    ry={Math.max(20, zone.height / 2 - 54)}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.035"
                    className="text-violet-500"
                  />
                  <g transform={`translate(${zone.x + 14}, ${zone.y - 15})`}>
                    <rect width="128" height="24" rx="12" fill="currentColor" fillOpacity="0.07" className="text-violet-500" />
                    <text x="12" y="16" fontSize="10" fontWeight="600" fill="currentColor" className="text-violet-500">
                      {zone.label} · {count}
                    </text>
                  </g>
                </g>
              )
            })}

            {visibleEdges.map((edge) => {
              const from = nodeById.get(edge.from)
              const to = nodeById.get(edge.to)
              const path = routedPaths.get(edge.id)
              if (!from || !to || !path) return null

              const connectedToSelection = edge.from === selectedId || edge.to === selectedId
              const active = (!focusMode || connectedToSelection)
                && (!searchTerm || isNodeActive(from) || isNodeActive(to))

              const opacity = !active
                ? 0.035
                : connectedToSelection
                  ? 0.82
                  : 0.2

              const width = connectedToSelection
                ? (edge.type === 'service' ? 2.2 : 1.8)
                : (edge.type === 'service' ? 1.2 : 0.95)

              return (
                <path
                  key={edge.id}
                  d={path}
                  fill="none"
                  stroke={edgeStroke(edge.type)}
                  strokeOpacity={opacity}
                  strokeWidth={width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                >
                  <title>{from.label} → {to.label} · {edge.bindingName} ({edge.type})</title>
                </path>
              )
            })}

            {visibleNodes.map((node) => {
              const active = isNodeActive(node)
              const selectedNode = selectedId === node.id
              const palette = nodePalette(node)
              const worker = node.kind === 'worker'
              const width = worker ? 126 : 92
              const height = worker ? 34 : 27
              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  opacity={active || selectedNode ? 1 : 0.13}
                  onClick={() => onSelect(node.id)}
                  className="cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') onSelect(node.id) }}
                >
                  {selectedNode && (
                    <rect
                      x={-width / 2 - 5}
                      y={-height / 2 - 5}
                      width={width + 10}
                      height={height + 10}
                      rx={worker ? 17 : 15}
                      fill={palette.glow}
                      fillOpacity="0.12"
                      stroke={palette.stroke}
                      strokeOpacity="0.7"
                      strokeWidth="1.5"
                      filter="url(#pm-node-glow)"
                    />
                  )}
                  <rect
                    x={-width / 2}
                    y={-height / 2}
                    width={width}
                    height={height}
                    rx={worker ? 13 : 14}
                    fill={palette.fill}
                    fillOpacity={worker ? 0.92 : 0.82}
                    stroke={palette.stroke}
                    strokeOpacity="0.72"
                  />
                  <circle cx={-width / 2 + 12} cy="0" r="3.2" fill={node.status === 'LIVE' ? '#34d399' : node.status === 'CONFIGURED' ? '#60a5fa' : '#f59e0b'} />
                  <text
                    x={-width / 2 + 20}
                    y="3.5"
                    fontSize={worker ? 9.4 : 8.6}
                    fontWeight="600"
                    fill="white"
                  >
                    {shortLabel(node.label, worker ? 19 : 12)}
                  </text>
                  <title>{node.label} · {node.kind === 'worker' ? `${node.degree} connected worker relationship(s)` : node.resourceType} · {node.status}</title>
                </g>
              )
            })}
          </svg>

          {selected && (
            <div className="pointer-events-none absolute right-4 top-4 hidden w-64 rounded-2xl border border-violet-500/15 bg-white/88 p-3 shadow-xl backdrop-blur-xl dark:bg-slate-950/88 md:block">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-text-primary">{selected.label}</div>
                  <div className="mt-0.5 text-[10px] text-text-muted">{selected.kind === 'worker' ? 'Worker' : selected.resourceType}</div>
                </div>
                <NodeStatusBadge status={selected.status} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                <div className="rounded-xl bg-violet-500/6 px-2.5 py-2">
                  <div className="text-text-muted">Connections</div>
                  <div className="mt-0.5 text-sm font-semibold text-text-primary">
                    {topology.edges.filter((edge) => edge.from === selected.id || edge.to === selected.id).length}
                  </div>
                </div>
                <div className="rounded-xl bg-violet-500/6 px-2.5 py-2">
                  <div className="text-text-muted">Zone</div>
                  <div className="mt-0.5 truncate text-[11px] font-semibold capitalize text-text-primary">{selected.zone}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-violet-500/10 px-4 py-3 text-[10px] text-text-muted">
        <span className="font-semibold text-text-secondary">Legend</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-violet-600" /> Workers</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> D1</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-cyan-500" /> KV</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> R2</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-pink-500" /> Vectorize</span>
        <span className="ml-auto">Visual zones are presentation-only; authority remains in the Platform Model evidence.</span>
      </div>
    </Card>
  )
}

function DetailMetric({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode
  label: string
  value: number | string
  hint: string
}) {
  return (
    <div className="rounded-2xl border border-violet-500/10 bg-white/68 p-3.5 backdrop-blur-xl dark:bg-slate-950/38">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
        <span className="text-violet-500">{icon}</span>{label}
      </div>
      <div className="mt-2 text-xl font-semibold text-text-primary">{value}</div>
      <div className="mt-0.5 text-[10px] text-text-muted">{hint}</div>
    </div>
  )
}

function bindingStatus(binding: PlatformModelDeclaredBinding, data: PlatformModel): NodeStatus {
  if (binding.type === 'service' && binding.target && data.items[binding.target]) return 'LIVE'
  return binding.target ? 'CONFIGURED' : 'UNKNOWN'
}

function DetailView({
  data,
  topology,
  selectedId,
}: {
  data: PlatformModel
  topology: TopologyData
  selectedId: string
}) {
  const node = topology.nodes.find((candidate) => candidate.id === selectedId) || topology.nodes[0]
  if (!node) return null

  const workerName = node.kind === 'worker' ? node.target : null
  const item: PlatformModelItem | undefined = workerName ? data.items[workerName] : undefined
  const bindings = item?.declared_bindings || []
  const endpoints = [...(item?.endpoints || [])].sort((a, b) => {
    const aUnknown = a.method === 'UNKNOWN' ? 1 : 0
    const bUnknown = b.method === 'UNKNOWN' ? 1 : 0
    return aUnknown - bUnknown || a.path_pattern.localeCompare(b.path_pattern) || a.method.localeCompare(b.method)
  })
  const routes = item?.routes || []

  const dependencyEdges = topology.edges.filter((edge) => edge.from === node.id && edge.to.startsWith('worker:'))
  const dependantEdges = topology.edges.filter((edge) => edge.to === node.id && edge.from.startsWith('worker:'))
  const connectedEdges = topology.edges.filter((edge) => edge.from === node.id || edge.to === node.id)

  const evidence = (item?.relationships || [])
    .map((relationship) => relationship.evidence)
    .filter((value): value is string => Boolean(value))
    .filter((value, index, all) => all.indexOf(value) === index)
    .slice(0, 6)

  const resourceWorkers = node.kind === 'resource'
    ? connectedEdges.map((edge) => edge.from.startsWith('worker:') ? edge.from.slice(7) : edge.to.slice(7)).filter((value, index, all) => all.indexOf(value) === index)
    : []

  const status: NodeStatus = item?.item_status === 'LIVE' ? 'LIVE' : node.status

  return (
    <Card className="overflow-hidden border-violet-500/10 bg-white/64 p-0 shadow-[0_22px_60px_rgba(76,29,149,0.06)] backdrop-blur-xl dark:bg-slate-950/42">
      <div className="flex flex-col gap-2 border-b border-violet-500/10 px-4 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Box className="h-4.5 w-4.5 text-violet-500" />
            <h3 className="text-sm font-semibold text-text-primary">Page / Detail View</h3>
          </div>
          <p className="mt-1 text-[11px] text-text-muted">Inspect the selected platform component and its current evidence-backed relationships.</p>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-text-muted">
          <span>{node.kind === 'worker' ? 'Workers' : 'Resources'}</span>
          <span>›</span>
          <span className="max-w-[280px] truncate font-medium text-text-primary">{node.label}</span>
        </div>
      </div>

      <CardContent className="p-4">
        <div className="grid gap-4 xl:grid-cols-[minmax(260px,0.8fr)_minmax(0,2.2fr)]">
          <div className="rounded-2xl border border-violet-500/10 bg-gradient-to-br from-violet-500/[0.08] via-white/55 to-blue-500/[0.05] p-4 dark:via-slate-950/30">
            <div className="flex items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-violet-500/20 bg-violet-500/12 text-violet-500 shadow-sm">
                {node.kind === 'worker' ? <Network className="h-5 w-5" /> : <Database className="h-5 w-5" />}
              </div>
              <div className="min-w-0">
                <div className="break-words text-sm font-semibold text-text-primary">{node.label}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <NodeStatusBadge status={status} />
                  <span className="rounded-full border border-border bg-surface/70 px-2 py-0.5 text-[9px] capitalize text-text-muted">
                    {node.kind === 'worker' ? 'Worker' : node.resourceType.replaceAll('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            <dl className="mt-5 space-y-2.5 text-[11px]">
              {item ? (
                <>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Public exposure</dt><dd className="text-right font-medium text-text-primary">{item.workers_dev ? 'workers.dev enabled' : 'Internal / no workers.dev'}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Compatibility date</dt><dd className="text-right font-medium text-text-primary">{item.compatibility_date || 'Unknown'}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Extraction</dt><dd className="text-right font-medium text-text-primary">{item.extraction_method || 'Unknown'}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Cron triggers</dt><dd className="max-w-[150px] text-right font-mono text-[10px] text-text-primary">{item.cron_triggers?.length ? item.cron_triggers.join(', ') : 'None observed'}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Worker routes</dt><dd className="max-w-[190px] text-right font-mono text-[9px] leading-relaxed text-text-primary">{routes.length ? routes.map((route) => route.pattern).join(', ') : 'None observed'}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Last observed</dt><dd className="text-right font-medium text-text-primary">{timeAgo(item.last_observed)}</dd></div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Target</dt><dd className="max-w-[170px] break-all text-right font-mono text-[10px] text-text-primary">{node.target}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Connected workers</dt><dd className="text-right font-medium text-text-primary">{resourceWorkers.length}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-text-muted">Observed via</dt><dd className="text-right font-medium text-text-primary">Declared binding</dd></div>
                </>
              )}
            </dl>

            {item?.discrepancies?.length ? (
              <div className="mt-4 space-y-2">
                {item.discrepancies.slice(0, 3).map((discrepancy, index) => (
                  <div key={`${discrepancy.type}-${index}`} className={`rounded-xl border px-3 py-2 text-[10px] ${discrepancy.severity === 'error' ? 'border-red-500/20 bg-red-500/8 text-red-500' : 'border-amber-500/20 bg-amber-500/8 text-amber-500'}`}>
                    <div className="flex items-center gap-1.5 font-semibold">
                      {discrepancy.severity === 'error' ? <AlertCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      {discrepancy.type}
                    </div>
                    <div className="mt-1 text-text-muted">{discrepancy.description}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <DetailMetric icon={<Globe className="h-3.5 w-3.5" />} label="Endpoints" value={endpoints.length} hint={item ? 'Source-observed routes' : 'Not applicable'} />
              <DetailMetric icon={<Link2 className="h-3.5 w-3.5" />} label="Bindings" value={bindings.length || connectedEdges.length} hint={item ? 'Declared live bindings' : 'Connected bindings'} />
              <DetailMetric icon={<Network className="h-3.5 w-3.5" />} label="Dependencies" value={dependencyEdges.length} hint="Worker targets" />
              <DetailMetric icon={<Network className="h-3.5 w-3.5 rotate-180" />} label="Dependants" value={dependantEdges.length} hint="Workers pointing here" />
            </div>

            <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.65fr)_minmax(260px,0.8fr)]">
              <div className="overflow-hidden rounded-2xl border border-violet-500/10 bg-surface/55">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div className="text-xs font-semibold text-text-primary">{item ? 'Bindings' : 'Connections'}</div>
                  <span className="text-[10px] text-text-muted">{item ? bindings.length : connectedEdges.length} observed</span>
                </div>

                {item ? (
                  bindings.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[560px] text-left text-[10px]">
                        <thead className="bg-violet-500/[0.035] text-text-muted">
                          <tr>
                            <th className="px-4 py-2 font-medium">Name</th>
                            <th className="px-3 py-2 font-medium">Type</th>
                            <th className="px-3 py-2 font-medium">Target</th>
                            <th className="px-3 py-2 font-medium">State</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bindings.map((binding) => (
                            <tr key={binding.name} className="border-t border-border/70">
                              <td className="px-4 py-2.5 font-mono font-medium text-violet-500">{binding.name}</td>
                              <td className="px-3 py-2.5 text-text-secondary">{binding.type}</td>
                              <td className="max-w-[260px] truncate px-3 py-2.5 font-mono text-text-secondary" title={binding.target || undefined}>{binding.target || '—'}</td>
                              <td className="px-3 py-2.5"><NodeStatusBadge status={bindingStatus(binding, data)} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <div className="px-4 py-8 text-center text-xs text-text-muted">No declared bindings observed.</div>
                ) : (
                  connectedEdges.length ? (
                    <div className="divide-y divide-border/70">
                      {connectedEdges.map((edge) => {
                        const otherId = edge.from === node.id ? edge.to : edge.from
                        const other = topology.nodes.find((candidate) => candidate.id === otherId)
                        return (
                          <div key={edge.id} className="flex items-center gap-3 px-4 py-3 text-[10px]">
                            <span className="font-mono font-medium text-violet-500">{edge.bindingName}</span>
                            <span className="text-text-muted">{edge.type}</span>
                            <span className="ml-auto truncate text-text-secondary">{other?.label || otherId}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : <div className="px-4 py-8 text-center text-xs text-text-muted">No connected worker bindings observed.</div>
                )}
              </div>

              <div className="rounded-2xl border border-violet-500/10 bg-surface/55 p-4">
                <div className="text-xs font-semibold text-text-primary">Structural Snapshot</div>
                <div className="mt-3 space-y-2 text-[10px]">
                  <div className="flex items-center justify-between gap-3"><span className="text-text-muted">Model generated</span><span className="font-medium text-text-primary">{timeAgo(data.generated_at)}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-text-muted">Graph schema</span><span className="font-mono text-text-primary">{data.graph_schema_version || 'legacy'}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-text-muted">Added entities</span><span className="font-medium text-emerald-500">+{data.change_from_previous?.added_entities || 0}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-text-muted">Removed entities</span><span className="font-medium text-red-500">-{data.change_from_previous?.removed_entities || 0}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-text-muted">Changed entities</span><span className="font-medium text-text-primary">{data.change_from_previous?.changed_entities || 0}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-text-muted">Relationship delta</span><span className="font-medium text-text-primary">+{data.change_from_previous?.added_relationships || 0} / -{data.change_from_previous?.removed_relationships || 0}</span></div>
                </div>
              </div>
            </div>

            {item && (
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="overflow-hidden rounded-2xl border border-violet-500/10 bg-surface/55">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div className="text-xs font-semibold text-text-primary">Endpoints</div>
                    <span className="text-[10px] text-text-muted">{endpoints.length} observed</span>
                  </div>
                  <div className="max-h-56 overflow-auto">
                    {endpoints.length ? endpoints.slice(0, 18).map((endpoint, index) => (
                      <div key={`${endpoint.path_pattern}-${endpoint.method}-${index}`} className="flex items-center gap-3 border-b border-border/60 px-4 py-2.5 last:border-0">
                        <span
                          title={endpoint.method === 'UNKNOWN' ? 'HTTP method not yet proven from the current source structure' : endpoint.confidence_rationale}
                          className={`min-w-20 rounded-md px-1.5 py-1 text-center font-mono text-[9px] font-semibold ${endpoint.method === 'UNKNOWN' ? 'bg-amber-500/8 text-amber-500' : 'bg-violet-500/8 text-violet-500'}`}
                        >
                          {endpoint.method === 'UNKNOWN' ? 'UNCONFIRMED' : endpoint.method}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-text-secondary" title={endpoint.path_pattern}>{endpoint.path_pattern}</span>
                        <span className="text-[9px] text-text-muted">{Math.round(endpoint.confidence * 100)}%</span>
                      </div>
                    )) : <div className="px-4 py-8 text-center text-xs text-text-muted">No endpoint patterns observed.</div>}
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-violet-500/10 bg-surface/55">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div className="text-xs font-semibold text-text-primary">Evidence</div>
                    <span className="text-[10px] text-text-muted">{item.relationships?.length || 0} relationship observations</span>
                  </div>
                  <div className="max-h-56 overflow-auto p-3">
                    {evidence.length ? (
                      <div className="space-y-2">
                        {evidence.map((entry, index) => (
                          <div key={index} className="rounded-xl border border-border bg-white/40 px-3 py-2.5 font-mono text-[9px] leading-relaxed text-text-secondary dark:bg-white/[0.025]">
                            {entry}
                          </div>
                        ))}
                      </div>
                    ) : <div className="px-2 py-5 text-center text-xs text-text-muted">No source relationship evidence recorded for this worker.</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function PlatformModelPanel() {
  const { data, isLoading, error } = usePlatformModel()
  const refresh = usePlatformModelRefresh()
  const [selectedId, setSelectedId] = useState('')

  const topology = useMemo(() => data ? buildTopology(data) : null, [data])

  const selected = useMemo(() => {
    if (!topology?.nodes.length) return ''
    if (selectedId && topology.nodes.some((node) => node.id === selectedId)) return selectedId

    const liveCore = topology.nodes.find((node) => node.id === 'worker:ops-bridge-core')
    if (liveCore) return liveCore.id

    const highestDegree = topology.nodes
      .filter((node) => node.kind === 'worker')
      .sort((a, b) => b.degree - a.degree)[0]
    return highestDegree?.id || topology.nodes[0].id
  }, [selectedId, topology])

  const totals = useMemo(() => {
    if (!data || !topology) return { workers: 0, relationships: 0, endpoints: 0, resources: 0, discrepancies: 0 }
    let endpoints = 0
    let discrepancies = 0
    Object.values(data.items).forEach((item) => {
      endpoints += item.endpoints?.length || 0
      discrepancies += item.discrepancies?.length || 0
    })
    return {
      workers: Object.keys(data.items).length,
      relationships: data.coverage?.relationships_found ?? topology.edges.length,
      endpoints,
      resources: topology.resourceCount,
      discrepancies,
    }
  }, [data, topology])

  if (isLoading) {
    return <div className="flex h-72 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-500" /></div>
  }

  if (error || !data || !topology) {
    return (
      <div className="px-3 py-5">
        <Card className="border-violet-500/10 bg-white/64 p-5 backdrop-blur-xl dark:bg-slate-950/42">
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5 text-violet-500" />
            <h2 className="text-lg font-semibold text-text-primary">Platform Model</h2>
          </div>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-text-muted">
            No current model is available. A full discovery reads the live Cloudflare worker inventory and deployed source, then rebuilds the map from evidence.
          </p>
          <button
            onClick={() => refresh.mutate(undefined)}
            disabled={refresh.isPending}
            className="mt-4 flex items-center gap-2 rounded-xl border border-violet-500/25 bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-500 transition hover:bg-violet-500/15 disabled:opacity-50"
          >
            {refresh.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Run platform discovery
          </button>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 px-2 py-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-2xl border border-violet-500/15 bg-gradient-to-br from-violet-500/15 to-blue-500/10 text-violet-500">
              <Network className="h-4.5 w-4.5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight text-text-primary">Platform Model</h2>
                <ModelStatusBadge status={data.model_status} />
              </div>
              <p className="mt-0.5 text-[11px] text-text-muted">Map, understand, and explore the live OPS platform.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/api/dashboard/platform-model/versions"
            target="_blank"
            rel="noreferrer"
            className="flex h-9 items-center gap-1.5 rounded-xl border border-border bg-surface/70 px-3 text-[11px] font-medium text-text-secondary transition hover:border-violet-500/25 hover:text-violet-500"
          >
            <Clock className="h-3.5 w-3.5" /> View history <ExternalLink className="h-3 w-3" />
          </a>
          <button
            onClick={() => refresh.mutate(undefined)}
            disabled={refresh.isPending}
            className="flex h-9 items-center gap-1.5 rounded-xl border border-violet-500/25 bg-violet-500/10 px-3 text-[11px] font-semibold text-violet-500 transition hover:bg-violet-500/15 disabled:opacity-50"
          >
            {refresh.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh model
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={<Network className="h-4 w-4" />} label="Workers" value={totals.workers} detail={data.coverage?.workers ? `${data.coverage.workers} discovered` : 'Live inventory'} />
        <StatCard icon={<Link2 className="h-4 w-4" />} label="Relationships" value={totals.relationships} detail={`${topology.serviceEdgeCount} worker-to-worker service links`} />
        <StatCard icon={<Globe className="h-4 w-4" />} label="Endpoints" value={totals.endpoints} detail="Source-observed route patterns" />
        <StatCard icon={<HardDrive className="h-4 w-4" />} label="Resources" value={totals.resources} detail={data.graph_summary ? `${data.graph_summary.entity_count} typed graph entities` : 'Declared resource targets'} />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-violet-500/10 bg-violet-500/[0.035] px-3 py-2 text-[10px] text-text-muted">
        <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> Generated {timeAgo(data.generated_at)}</span>
        <span>Graph schema {data.graph_schema_version || 'legacy'}</span>
        <span>{totals.discrepancies} discrepancy finding{totals.discrepancies === 1 ? '' : 's'}</span>
        <span className="ml-auto hidden sm:inline">Current structural evidence, not model recollection.</span>
      </div>

      <TopologyGraph data={data} selectedId={selected} onSelect={setSelectedId} />
      <DetailView data={data} topology={topology} selectedId={selected} />
    </div>
  )
}
